/**
 * Супервизор веб-сокета до станции.
 *
 * Встроенный реконнект SIP.js молча сдаётся после нескольких попыток и не различает
 * «сокет упал» и «сокет жив, но станция недоступна». Поэтому переподключение своё:
 * экспоненциальная пауза 1 → 30 с с jitter (спека §7.9) и запрет переподключаться
 * во время активного звонка — рвать разговор ради восстановления сокета нельзя.
 */
import { systemScheduler } from './ports.js';
export const TRANSPORT_MIN_DELAY_MS = 1_000;
export const TRANSPORT_MAX_DELAY_MS = 30_000;
/**
 * Пауза перед попыткой номер attempt (1 — первая).
 * Удвоение от минимальной с потолком, плюс случайная добавка до jitterRatio,
 * чтобы двадцать пультов не ломились на станцию одной секундой после её рестарта.
 */
export function nextBackoffDelay(attempt, options) {
    const min = options.minMs ?? TRANSPORT_MIN_DELAY_MS;
    const max = options.maxMs ?? TRANSPORT_MAX_DELAY_MS;
    const jitterRatio = options.jitterRatio ?? 0.3;
    const base = Math.min(max, min * 2 ** Math.max(0, attempt - 1));
    return Math.round(base * (1 + jitterRatio * options.random()));
}
export class TransportSupervisor {
    #transport;
    #options;
    #scheduler;
    #unsubscribe = null;
    #cancelTimer = null;
    #attempt = 0;
    #running = false;
    constructor(transport, options) {
        this.#transport = transport;
        this.#options = options;
        this.#scheduler = options.scheduler ?? systemScheduler;
    }
    /** Номер текущей попытки переподключения; 0 — соединение живо */
    get attempt() {
        return this.#attempt;
    }
    start() {
        if (this.#running)
            return;
        this.#running = true;
        this.#unsubscribe = this.#transport.onStateChange((state) => {
            this.#options.onState(state);
            if (state === 'connected') {
                this.#attempt = 0;
                return;
            }
            if (state === 'disconnected')
                this.#scheduleReconnect();
        });
        void this.#tryConnect();
    }
    stop() {
        this.#running = false;
        this.#cancelTimer?.();
        this.#cancelTimer = null;
        this.#unsubscribe?.();
        this.#unsubscribe = null;
        void this.#transport.disconnect();
    }
    async #tryConnect() {
        if (!this.#running)
            return;
        this.#attempt += 1;
        try {
            await this.#transport.connect();
        }
        catch (error) {
            this.#options.onError?.(error, this.#attempt);
            this.#scheduleReconnect();
        }
    }
    #scheduleReconnect() {
        if (!this.#running || this.#cancelTimer !== null)
            return;
        // Во время разговора пересоздание транспорта рвёт сессию: ждём и перепроверяем
        // коротким интервалом, счётчик попыток при этом не растёт.
        const callActive = this.#options.isCallActive();
        const delay = callActive
            ? TRANSPORT_MIN_DELAY_MS
            : nextBackoffDelay(this.#attempt, {
                ...(this.#options.minDelayMs !== undefined ? { minMs: this.#options.minDelayMs } : {}),
                ...(this.#options.maxDelayMs !== undefined ? { maxMs: this.#options.maxDelayMs } : {}),
                ...(this.#options.jitterRatio !== undefined
                    ? { jitterRatio: this.#options.jitterRatio }
                    : {}),
                random: () => this.#scheduler.random(),
            });
        this.#cancelTimer = this.#scheduler.schedule(() => {
            this.#cancelTimer = null;
            if (this.#options.isCallActive()) {
                this.#scheduleReconnect();
                return;
            }
            void this.#tryConnect();
        }, delay);
    }
}
//# sourceMappingURL=transport-supervisor.js.map